import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTheme } from "../context/ThemeContext";
import {
  FolderKanban,
  CheckCircle2,
  AlertTriangle,
  Banknote,
  Receipt,
  Clock,
  ArrowUpRight,
  TrendingUp,
  TrendingDown,
  Truck,
  Users,
  Hammer,
  Package,
  Filter,
  Calendar,
  Award,
  DollarSign,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatCompact = (value) => {
  if (value >= 1000000000) return `₱${(value / 1000000000).toFixed(1)}B`;
  if (value >= 1000000) return `₱${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `₱${(value / 1000).toFixed(1)}K`;
  return `₱${value}`;
};

const EXPENSE_CATEGORIES = ["Labor", "Pakyaw", "Materials", "Aggregates", "Equipment"];
const PIE_COLORS = ["#DC143C", "#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cashFlowData, setCashFlowData] = useState([]);
  const [costBreakdown, setCostBreakdown] = useState({});
  const [projectCategories, setProjectCategories] = useState([]);
  const [contractDistribution, setContractDistribution] = useState([]);
  const { theme } = useTheme();

  // Filter states
  const [licenseFilter, setLicenseFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [availableLicenses, setAvailableLicenses] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const filtersRes = await axios.get(`${API}/dashboard/filters`, { withCredentials: true });
        setAvailableLicenses(filtersRes.data.licenses || []);
        setAvailableYears(filtersRes.data.years || []);
      } catch (error) {
        console.error("Error fetching filters:", error);
      }
    };
    fetchFilters();
  }, []);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/seed`, {}, { withCredentials: true });

      const params = new URLSearchParams();
      if (licenseFilter && licenseFilter !== "all") params.append("license", licenseFilter);
      if (yearFilter && yearFilter !== "all") params.append("year", yearFilter);

      const statsRes = await axios.get(`${API}/dashboard/stats?${params.toString()}`, { withCredentials: true });
      setStats(statsRes.data);

      const filteredProjectIds = statsRes.data.filtered_project_ids || [];
      const projectIdsParam = filteredProjectIds.length > 0 ? filteredProjectIds.join(",") : "";

      const dataParams = new URLSearchParams();
      if (projectIdsParam && (licenseFilter !== "all" || yearFilter !== "all")) {
        dataParams.append("project_ids", projectIdsParam);
      }
      if (yearFilter && yearFilter !== "all") {
        dataParams.append("year", yearFilter);
      }

      const [expensesRes, billingsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/expenses?${dataParams.toString()}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/billings?${dataParams.toString()}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/projects`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);

      let filteredProjects = projectsRes.data || [];
      if (licenseFilter && licenseFilter !== "all") {
        filteredProjects = filteredProjects.filter((p) => p.license === licenseFilter);
      }
      if (yearFilter && yearFilter !== "all") {
        filteredProjects = filteredProjects.filter((p) => {
          const dateFields = [p.notice_of_award, p.bid_date, p.start_date];
          return dateFields.some((d) => d && d.startsWith(yearFilter));
        });
      }

      // Cash Flow
      const monthlyData = {};
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = d.toISOString().slice(0, 7);
        const label = d.toLocaleDateString("en-PH", { month: "short" });
        monthlyData[key] = { month: label, billing: 0, expenses: 0 };
      }
      (billingsRes.data || []).forEach((bill) => {
        const date = bill.billing_period || bill.created_at;
        if (date) {
          const key = date.slice(0, 7);
          if (monthlyData[key]) monthlyData[key].billing += bill.gross_amount || 0;
        }
      });
      (expensesRes.data || []).forEach((exp) => {
        const key = exp.date?.slice(0, 7);
        if (key && monthlyData[key]) monthlyData[key].expenses += exp.amount || 0;
      });
      setCashFlowData(Object.values(monthlyData));

      // Cost Breakdown
      const breakdown = {};
      EXPENSE_CATEGORIES.forEach((cat) => (breakdown[cat] = 0));
      breakdown["Others"] = 0;
      (expensesRes.data || []).forEach((exp) => {
        const cat = EXPENSE_CATEGORIES.includes(exp.category) ? exp.category : "Others";
        breakdown[cat] += exp.amount || 0;
      });
      setCostBreakdown(breakdown);

      // Project Categories
      const categoryMap = {};
      filteredProjects.forEach((p) => {
        const cat = p.category || "Uncategorized";
        if (!categoryMap[cat]) categoryMap[cat] = { name: cat, value: 0, count: 0 };
        categoryMap[cat].value += p.contract_amount || 0;
        categoryMap[cat].count += 1;
      });
      setProjectCategories(Object.values(categoryMap));

      // Contract Distribution
      const statusMap = {};
      filteredProjects.forEach((p) => {
        const status = p.status || "Unknown";
        if (!statusMap[status]) statusMap[status] = { name: status, value: 0, count: 0 };
        statusMap[status].value += p.contract_amount || 0;
        statusMap[status].count += 1;
      });
      setContractDistribution(Object.values(statusMap));

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, [licenseFilter, yearFilter]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const chartColors = {
    gridStroke: theme === "dark" ? "#333" : "#e5e7eb",
    tickFill: theme === "dark" ? "#A1A1AA" : "#6b7280",
    tooltipBg: theme === "dark" ? "#121212" : "#ffffff",
    tooltipBorder: theme === "dark" ? "#333" : "#e5e7eb",
    barPrimary: theme === "dark" ? "#DC143C" : "#2563eb",
    barSecondary: theme === "dark" ? "#71717A" : "#94a3b8",
    linePrimary: theme === "dark" ? "#22C55E" : "#10b981",
  };

  const totalExpenses = Object.values(costBreakdown).reduce((sum, val) => sum + val, 0);
  const totalRevenue  = cashFlowData.reduce((sum, m) => sum + (m.billing || 0), 0);
  const totalProfit   = totalRevenue - totalExpenses;

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--ring))]"></div>
      </div>
    );
  }

  const projectCards = [
    { title: "Active Projects",  value: stats?.active_projects    || 0, icon: FolderKanban, color: "text-emerald-500", bgColor: "bg-emerald-500/10" },
    { title: "Completed",        value: stats?.completed_projects || 0, icon: CheckCircle2, color: "text-gray-400",    bgColor: "bg-gray-500/10" },
    { title: "Delayed",          value: stats?.delayed_projects   || 0, icon: AlertTriangle, color: "text-red-500",   bgColor: "bg-red-500/10" },
  ];

  const financialCards = [
    { title: "Total Contract", value: formatCompact(stats?.total_contract_amount   || 0), full: formatCurrency(stats?.total_contract_amount   || 0), icon: Banknote,                              color: "text-foreground",              bgColor: "bg-muted" },
    { title: "Total Billed",   value: formatCompact(stats?.total_billed_amount     || 0), full: formatCurrency(stats?.total_billed_amount     || 0), icon: Receipt,                               color: "text-[hsl(var(--secondary))]", bgColor: "bg-[hsl(var(--secondary))]/10" },
    { title: "Outstanding",    value: formatCompact(stats?.outstanding_receivables || 0), full: formatCurrency(stats?.outstanding_receivables || 0), icon: Clock,                                 color: "text-amber-500",               bgColor: "bg-amber-500/10" },
    { title: "Total Profit",   value: (totalProfit >= 0 ? "+" : "") + formatCompact(Math.abs(totalProfit)),                                          full: formatCurrency(totalProfit),            icon: totalProfit >= 0 ? TrendingUp : TrendingDown, color: totalProfit >= 0 ? "text-green-500" : "text-red-500", bgColor: totalProfit >= 0 ? "bg-green-500/10" : "bg-red-500/10" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-fade-in">

      {/* Page Header with Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Overview of all construction projects</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={licenseFilter} onValueChange={setLicenseFilter}>
            <SelectTrigger data-testid="license-filter" className="w-[180px] input-streamcon">
              <Award className="w-4 h-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Filter by License" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Licenses</SelectItem>
              {availableLicenses.map((lic) => <SelectItem key={lic} value={lic}>{lic}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger data-testid="year-filter" className="w-[140px] input-streamcon">
              <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Filter by Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {availableYears.map((year) => <SelectItem key={year} value={year}>{year}</SelectItem>)}
            </SelectContent>
          </Select>
          <Link to="/projects">
            <Button data-testid="view-all-projects-btn" className="btn-primary flex items-center gap-2">
              View All Projects <ArrowUpRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Active Filter Indicator */}
      {(licenseFilter !== "all" || yearFilter !== "all") && (
        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-sm">
          <Filter className="w-4 h-4 text-[hsl(var(--secondary))]" />
          <span className="text-sm text-muted-foreground">
            Filtered by:{" "}
            {licenseFilter !== "all" && <span className="text-foreground font-medium">License: {licenseFilter}</span>}
            {licenseFilter !== "all" && yearFilter !== "all" && " • "}
            {yearFilter !== "all" && <span className="text-foreground font-medium">Year: {yearFilter}</span>}
          </span>
          <Button variant="ghost" size="sm" className="ml-auto text-xs"
            onClick={() => { setLicenseFilter("all"); setYearFilter("all"); }}>
            Clear Filters
          </Button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 grid grid-cols-3 gap-4">
          {projectCards.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <Card key={kpi.title} data-testid={`kpi-${kpi.title.toLowerCase().replace(/\s+/g, "-")}`} className="card-streamcon">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`p-2 rounded-sm ${kpi.bgColor}`}>
                      <Icon className={`w-4 h-4 ${kpi.color}`} />
                    </div>
                  </div>
                  <div className="kpi-value text-foreground">{kpi.value}</div>
                  <div className="kpi-label mt-1">{kpi.title}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <div className="flex flex-col gap-4">
          {financialCards.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <Card key={kpi.title} data-testid={`kpi-${kpi.title.toLowerCase().replace(/\s+/g, "-")}`} className="card-streamcon flex-1">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`p-2 rounded-sm flex-shrink-0 ${kpi.bgColor}`}>
                    <Icon className={`w-4 h-4 ${kpi.color}`} />
                  </div>
                  <div className="min-w-0">
                    <div className={`text-lg font-bold font-mono truncate ${kpi.color}`}>{kpi.value}</div>
                    <div className="kpi-label">{kpi.title}</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="card-streamcon">
          <CardHeader className="card-header-streamcon">
            <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[hsl(var(--secondary))]" />
              Cash Flow (Billing vs Expenses)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={cashFlowData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.gridStroke} />
                  <XAxis dataKey="month" tick={{ fill: chartColors.tickFill, fontSize: 10 }} axisLine={{ stroke: chartColors.gridStroke }} />
                  <YAxis tick={{ fill: chartColors.tickFill, fontSize: 10 }} axisLine={{ stroke: chartColors.gridStroke }} tickFormatter={(v) => formatCompact(v)} />
                  <Tooltip contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.tooltipBorder}`, borderRadius: "4px" }} labelStyle={{ color: theme === "dark" ? "#fff" : "#000" }} formatter={(value, name) => [formatCurrency(value), name === "billing" ? "Billing (Income)" : "Expenses"]} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="expenses" name="Expenses" fill={chartColors.barSecondary} radius={[2, 2, 0, 0]} />
                  <Line type="monotone" dataKey="billing" name="Billing" stroke={chartColors.linePrimary} strokeWidth={2} dot={{ fill: chartColors.linePrimary, strokeWidth: 2, r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="card-streamcon">
          <CardHeader className="card-header-streamcon">
            <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
              <Receipt className="w-4 h-4 text-[hsl(var(--secondary))]" />
              Billing Trend
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cashFlowData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.gridStroke} />
                  <XAxis dataKey="month" tick={{ fill: chartColors.tickFill, fontSize: 10 }} axisLine={{ stroke: chartColors.gridStroke }} />
                  <YAxis tick={{ fill: chartColors.tickFill, fontSize: 10 }} axisLine={{ stroke: chartColors.gridStroke }} tickFormatter={(v) => formatCompact(v)} />
                  <Tooltip contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.tooltipBorder}`, borderRadius: "4px" }} labelStyle={{ color: theme === "dark" ? "#fff" : "#000" }} formatter={(value) => [formatCurrency(value), "Billing"]} />
                  <Line type="monotone" dataKey="billing" stroke={chartColors.barPrimary} strokeWidth={2} dot={{ fill: chartColors.barPrimary, strokeWidth: 2 }} activeDot={{ r: 6, fill: chartColors.barPrimary }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pie Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="card-streamcon">
          <CardHeader className="card-header-streamcon">
            <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
              <FolderKanban className="w-4 h-4 text-[hsl(var(--secondary))]" />
              Project Categories (%)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-[250px]">
              {projectCategories.length === 0 ? (
                <div className="h-full flex items-center justify-center text-muted-foreground">No project data available</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={projectCategories} cx="50%" cy="50%" labelLine={false} outerRadius={80} dataKey="count" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(2)}%`}>
                      {projectCategories.map((_, index) => <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.tooltipBorder}`, borderRadius: "4px", color: theme === "dark" ? "#fff" : "#000" }} itemStyle={{ color: theme === "dark" ? "#fff" : "#000" }} labelStyle={{ color: theme === "dark" ? "#fff" : "#000" }} formatter={(value, name, props) => [`${value} projects (${formatCurrency(props.payload.value)})`, props.payload.name]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-2">
              {projectCategories.map((cat, idx) => (
                <div key={cat.name} className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                  <span className="text-xs text-muted-foreground">{cat.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="card-streamcon">
          <CardHeader className="card-header-streamcon">
            <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
              <Banknote className="w-4 h-4 text-[hsl(var(--secondary))]" />
              Total Contracts by Status (%)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-[250px]">
              {contractDistribution.length === 0 ? (
                <div className="h-full flex items-center justify-center text-muted-foreground">No contract data available</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={contractDistribution} cx="50%" cy="50%" labelLine={false} outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(2)}%`}>
                      {contractDistribution.map((_, index) => <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.tooltipBorder}`, borderRadius: "4px", color: theme === "dark" ? "#fff" : "#000" }} itemStyle={{ color: theme === "dark" ? "#fff" : "#000" }} labelStyle={{ color: theme === "dark" ? "#fff" : "#000" }} formatter={(value, name, props) => [`${formatCurrency(value)} (${props.payload.count} projects)`, props.payload.name]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-2">
              {contractDistribution.map((item, idx) => (
                <div key={item.name} className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                  <span className="text-xs text-muted-foreground">{item.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}